import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Building2, Globe, MapPin, Users, UserCheck, Wrench, CalendarDays } from "lucide-react";
import { motion } from "framer-motion";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getLogoUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${API_URL}${url}`;
};

function ClientCard({ client }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const cardRef = useRef(null);
  const timeoutRef = useRef(null);

  const logoUrl = getLogoUrl(client.logo_url);
  const initials = (client.name || "")
    .split(/[\s&]+/)
    .filter(w => w.length > 1)
    .slice(0, 2)
    .map(w => w[0])
    .join("")
    .toUpperCase();

  const hasDetails = client.about || client.state || client.employee_count || (client.services_provided && client.services_provided.length > 0) || client.onboard_date;

  const yearsWithAbsolin = (() => {
    if (!client.onboard_date) return null;
    const onboard = new Date(client.onboard_date);
    const now = new Date();
    const diffYears = (now - onboard) / (365.25 * 24 * 60 * 60 * 1000);
    return Math.ceil(diffYears);
  })();

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        setTooltipPos({
          top: rect.top - 8,
          left: rect.left + rect.width / 2
        });
      }
      setShowTooltip(true);
    }, 300);
  };

  const handleMouseLeave = () => {
    clearTimeout(timeoutRef.current);
    setShowTooltip(false);
  };

  return (
    <div
      ref={cardRef}
      className="group relative flex flex-col items-center gap-2 p-3 rounded-xl border border-slate-100 hover:border-[#0066CC]/30 hover:shadow-md transition-all duration-200 bg-white cursor-default"
      data-testid={`client-card-${client.id}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden group-hover:border-[#0066CC]/20 transition-colors">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={client.name}
            className="w-full h-full object-contain p-1.5"
            onError={(e) => { e.target.style.display = "none"; if (e.target.nextSibling) e.target.nextSibling.style.display = "flex"; }}
          />
        ) : null}
        <span
          className={`text-sm sm:text-base font-bold text-[#0066CC]/70 ${logoUrl ? "hidden" : "flex"}`}
          style={{ display: logoUrl ? "none" : "flex" }}
        >
          {initials}
        </span>
      </div>
      <div className="text-center w-full">
        <p className="text-[10px] sm:text-xs font-semibold text-slate-800 line-clamp-2 leading-tight group-hover:text-[#0066CC] transition-colors">
          {client.name}
        </p>
        {client.state && (
          <p className="text-[9px] sm:text-[10px] text-slate-400 mt-0.5 flex items-center justify-center gap-0.5">
            <MapPin className="w-2.5 h-2.5" />
            {client.state}
          </p>
        )}
      </div>

      {/* Hover Tooltip - rendered via portal to avoid overflow clipping */}
      {showTooltip && hasDetails && createPortal(
        <div
          style={{ position: 'fixed', top: tooltipPos.top, left: tooltipPos.left, transform: 'translate(-50%, -100%)', zIndex: 9999 }}
          onMouseEnter={() => clearTimeout(timeoutRef.current)}
          onMouseLeave={handleMouseLeave}
          data-testid={`client-tooltip-${client.id}`}
        >
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.15 }}
            className="w-72"
          >
            <div className="bg-slate-900 text-white rounded-xl shadow-2xl border border-slate-700/50 overflow-hidden">
              {/* Header */}
              <div className="px-4 pt-3 pb-2 border-b border-slate-700/50">
                <p className="font-semibold text-sm text-white truncate">{client.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {client.industry && (
                    <p className="text-[11px] text-slate-400">{client.industry}</p>
                  )}
                  {yearsWithAbsolin && yearsWithAbsolin > 0 && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500/20 text-emerald-300 rounded text-[10px] font-semibold whitespace-nowrap">
                      <CalendarDays className="w-2.5 h-2.5" />
                      {yearsWithAbsolin} {yearsWithAbsolin === 1 ? 'Year' : 'Years'} Journey
                    </span>
                  )}
                </div>
              </div>

              <div className="px-4 py-2.5 space-y-2">
                {/* About */}
                {client.about && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-0.5">About</p>
                    <p className="text-xs text-slate-300 leading-relaxed line-clamp-3">{client.about}</p>
                  </div>
                )}

                {/* Details Row */}
                <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                  {client.state && (
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-3 h-3 text-[#4d9dff]" />
                      <span className="text-xs text-slate-300">{client.state}</span>
                    </div>
                  )}
                  {client.employee_count && (
                    <div className="flex items-center gap-1.5">
                      <UserCheck className="w-3 h-3 text-emerald-400" />
                      <span className="text-xs text-slate-300">{client.employee_count} employees</span>
                    </div>
                  )}
                </div>

                {/* Services Provided */}
                {client.services_provided && client.services_provided.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1 flex items-center gap-1">
                      <Wrench className="w-2.5 h-2.5" /> Services
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {client.services_provided.map((svc, i) => (
                        <span key={i} className="px-1.5 py-0.5 bg-[#0066CC]/20 text-[#4d9dff] rounded text-[10px] font-medium">
                          {svc}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Website */}
                {client.website && (
                  <div className="flex items-center gap-1.5">
                    <Globe className="w-3 h-3 text-slate-500" />
                    <a href={client.website} target="_blank" rel="noopener noreferrer" className="text-xs text-[#4d9dff] hover:underline truncate">
                      {client.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                    </a>
                  </div>
                )}
              </div>

              {/* Arrow */}
              <div className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-3 bg-slate-900 rotate-45 border-r border-b border-slate-700/50" />
            </div>
          </motion.div>
        </div>,
        document.body
      )}
    </div>
  );
}

export const ClientLogosWithProfiles = ({ clients }) => {
  if (!clients || clients.length === 0) return null;

  return (
    <div className="relative" data-testid="client-logos-profiles">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {clients.map((client) => (
          <ClientCard key={client.id} client={client} />
        ))}
      </div>

      {/* Mobile: Tap instruction */}
      <p className="text-center text-slate-400 text-xs mt-6 md:hidden">
        Tap on logos to see client details
      </p>
    </div>
  );
};

// Fallback for old ClientLogos (just logos without profiles)
export const ClientLogos = ({ logos }) => {
  if (!logos || logos.length === 0) return null;

  return (
    <div className="py-8" data-testid="client-logos">
      <h3 className="text-lg font-semibold text-slate-900 mb-6 text-center">Our Clients</h3>
      <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
        {logos.map((logo) => (
          <div
            key={logo.id}
            className="client-logo flex items-center justify-center h-16 w-32"
            data-testid={`client-logo-${logo.id}`}
          >
            <img
              src={logo.logo_url}
              alt={logo.name}
              className="max-h-full max-w-full object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
